import { FileCategory } from '@aionzdev/design-system';
import { UserResponse } from '@aionzdev/modules';
import { ProductIntegrationStatus, PurchaseRequestStatus } from '../enums/index';
import { AddSupplierToProductDto } from '../interfaces/add-supplier-to-product.dto';
import { ApproveQuotationMap } from '../interfaces/approve-quotation-map';
import { BuyerItem } from '../interfaces/buyer-item.interface';
import { CustomerTradingFocItem } from '../interfaces/customer-trading-foc-dialog-data';
import { DuplicatedProductResponseDto } from '../interfaces/duplicated-product-response.dto';
import { MergePurchaseRequestItemsDto } from '../interfaces/merge-purchase-request-items.dto';
import { OrderHistoryItem } from '../interfaces/order-history-item';
import { ProductIntegrationData, ProductIntegrationDialogData } from '../interfaces/product-integration-dialog-data';
import { PurchaseHistory } from '../interfaces/purchase-history';
import { PurchaseItemStatus, PurchaseRequest, PurchaseRequestFile } from '../interfaces/purchase-request';
import { AttachmentOriginType, PurchaseRequestAttachmentGroup } from '../interfaces/purchase-request-attachment-group';
import { PurchaseRequestAuditDto, PurchaseRequestAuditStatus } from '../interfaces/purchase-request-audit.dto';
import { PurchaseRequestBranch } from '../interfaces/purchase-request-branch';
import { PurchaseRequestCommentAttachmentDto, PurchaseRequestCommentDto } from '../interfaces/purchase-request-comment.dto';
import { PurchaseRequestDocumentUploadItemResult, PurchaseRequestDocumentUploadItemType } from '../interfaces/purchase-request-document-upload-item-result';
import { PurchaseRequestResponseDto } from '../interfaces/purchase-response.dto';
import { PurchaseSupplier, PurchaseSupplierCreate, PurchaseSupplierLinkStatus, PurchaseSupplierSource } from '../interfaces/purchase-supplier';
import { PurchasingEvent } from '../interfaces/purchasing-event';
import { QuotationMapData } from '../interfaces/quotation-map-data';
import { QuotationMapItem } from '../interfaces/quotation-map-item';
import { branchLabel } from '../admin/utils/branch-label.util';
import { QuotationMapSupplier, QuotationMapSupplierStatus } from '../interfaces/quotation-map-supplier';
import { QuotationMapSupplierFreight } from '../interfaces/quotation-map-supplier-freight';
import { SendPurchaseRequestForQuoteItem } from '../interfaces/send-purchase-request-for-quote-dialog-data';
import { SupplierInfo } from '../interfaces/supplier-info';
import { TimePerStageMetrics } from '../interfaces/time-per-stage-metrics';
import { TradingMatchSuggestion } from '../interfaces/trading-match-suggestion';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Endereco } from '../../../core/models/endereco';
import { Usuario } from '../../../core/models/usuario';
import { AcceptOffersRequestDto } from '../interfaces/dtos/accept-offers-request.dto';
import { BuyerResponseDto } from '../interfaces/dtos/buyer-response.dto';
import { CheckDuplicatedProductsResponseDto } from '../interfaces/dtos/check-duplicates-response.dto';
import { CustomerTradingFocResponseDto } from '../interfaces/dtos/customer-trading-foc-response.dto';
import {
  FindAllPurchaseRequestOffersResponseDto,
  OfferResponseDto,
  OrderAttributeResponseDto,
} from '../interfaces/dtos/find-all-purchase-offers-response.dto';
import { FindAllPurchaseProductsResponseDto } from '../interfaces/dtos/find-all-purchase-products-response.dto';
import {
  FindAllPurchaseOffersResponseDto,
  PurchaseAddressResponseDto,
  PurchaseOrdersHistory,
  PurchaseStageTimesResponseDto,
} from '../interfaces/dtos/find-all-purchase-response.dto';
import { FIndOfferResponseDto, FornecedorParticipanteDto } from '../interfaces/dtos/find-offer-response.dto';
import { UncompletedPurchaseResponseDto } from '../interfaces/dtos/find-uncompleted-purchases-response.dto';
import { HistoricoComprasDto } from '../interfaces/dtos/historico-compras.dto';
import { MergePurchasesRequestDto } from '../interfaces/dtos/merge-purchases-request.dto';
import { NewSupplierRequestDto } from '../interfaces/dtos/new-supplier-request.dto';
import { PurchaseRequestAuditResponseDto } from '../interfaces/dtos/purchase-request-audit-response.dto';
import {
  OfertaCommentAttachmentType,
  PurchaseRequestCommentResponseDto,
} from '../interfaces/dtos/purchase-request-comment-response.dto';
import { SpreadSheetStatusDto } from '../interfaces/dtos/spread-sheet-status.dto';
import { StageTimeMetricsResponseDto } from '../interfaces/dtos/stage-time-metrics-response.dto';
import { SupplierInfoResponseDto } from '../interfaces/dtos/supplier-info-response.dto';
import { QuotationSupplierCacheEntry } from '../interfaces/quotation-supplier-cache-entry';
import { TradingMatchSuggestionResponseDto } from '../interfaces/dtos/trading-match-suggestion-response.dto';

@Injectable({
  providedIn: 'root',
})
export class PurchasingMapperService {
  private extractOrderErpCode(attributes?: Array<OrderAttributeResponseDto>): string | undefined {
    if (!attributes?.length) return undefined;

    const normalize = (value: string): string =>
      value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();

    const erpAttribute = attributes.find((attribute) => {
      if (!attribute.nome) return false;
      const name = normalize(attribute.nome);
      return (
        name.includes('cod erp do produto') ||
        name.includes('codigo erp do produto') ||
        name.includes('cod. erp') ||
        name.includes('codigo erp')
      );
    });

    const code = erpAttribute?.valor?.trim();
    return code ? code : undefined;
  }

  public toPurchaseRequestAttachmentGroups(
    orders: FindAllPurchaseProductsResponseDto[],
  ): PurchaseRequestAttachmentGroup[] {
    const groupsMap = new Map<string, PurchaseRequestAttachmentGroup>();

    for (const order of orders) {
      for (const attachment of order.anexos || []) {
        const originType =
          attachment.autor?.tipo?.toLowerCase() === 'solicitante'
            ? AttachmentOriginType.REQUESTER
            : AttachmentOriginType.SUPPLIER;

        const authorId = attachment.autor?.id?.toString() ?? 'unknown';
        const authorName =
          attachment.autor?.nome ??
          (originType === AttachmentOriginType.REQUESTER ? 'Solicitante' : 'Fornecedor');
        const key = `${originType}:${authorId}`;

        const currentGroup = groupsMap.get(key) ?? {
          name: authorName,
          avatar: attachment.autor?.avatar,
          type: originType,
          attachments: [],
        };

        currentGroup.attachments.push({
          id: attachment.id.toString(),
          fileName: attachment.name,
          fileType: attachment.mimeType,
          uploadedAt: attachment.createdAt,
          url: attachment.url,
        });

        groupsMap.set(key, currentGroup);
      }
    }

    return Array.from(groupsMap.values());
  }

  public toQuotationMapItems(
    data: FindAllPurchaseRequestOffersResponseDto,
    suppliersInfo: SupplierInfo[] = [],
    fornecedoresParticipantes: FornecedorParticipanteDto[] = [],
  ): QuotationMapItem[] {
    const { pedidos, fornecedores, ofertas } = data;

    const offersMap = new Map<string, OfferResponseDto>();
    for (const oferta of ofertas) {
      offersMap.set(`${oferta.productFinal.productId}-${oferta.usuarioId}`, oferta);
    }

    const quotationMapItems: QuotationMapItem[] = pedidos.map((pedido) => {
      const suppliers = new Map<string, QuotationMapSupplier>();

      for (const fornecedor of fornecedores) {
        const offer = offersMap.get(`${pedido.produtoId}-${fornecedor.id}`);
        const fp = fornecedoresParticipantes.find(
          (p) => p.fornecedorId === fornecedor.id || p.fornecedor?.id === fornecedor.id,
        );
        const cnpj = fp?.fornecedor?.cpf;
        const phone = fp?.fornecedor?.telefone || fornecedor.telefone;

        const info = suppliersInfo.find((s) => {
          const matchesEmail =
            !!s.email &&
            !!fornecedor.email &&
            s.email.toLowerCase() === fornecedor.email.toLowerCase();
          const matchesName =
            s.name.toLowerCase() === fornecedor.nome.toLowerCase() ||
            (!!fornecedor.nomeFantasia &&
              s.name.toLowerCase() === fornecedor.nomeFantasia.toLowerCase());

          return matchesEmail || matchesName;
        });

        const suggestedPending = !!pedido.fornecedoresPendentesIds?.includes(fornecedor.id);
        const notParticipating =
          !suggestedPending &&
          !!pedido.fornecedoresIds?.length &&
          !pedido.fornecedoresIds.includes(fornecedor.id);

        const supplierData: QuotationMapSupplier = offer
          ? {
              id: String(fornecedor.id),
              offerId: String(offer.id),
              companyName: fornecedor.nomeFantasia || fornecedor.nome,
              status: QuotationMapSupplierStatus.RESPONDED,
              respondedAt: offer.createdAt,
              validity: offer.expiresIn,
              price: offer.productFinal.price,
              originalPrice: offer.productFinal.oldPrice ?? null,
              observations: offer.obs || offer.obsOriginal || undefined,
              selected: false,
              negotiatedPrice: undefined,
              negotiatedSelected: undefined,
              paymentMethod: offer.formaPagamentoObj?.nome,
              paymentTerms: offer.prazoPagamentoObj?.nome,
              email: info?.email,
              link: info?.link,
              notParticipating,
              cnpj,
              phone,
              suggestedPending,
              ofertaFornecedorId: String(fornecedor.ofertaFornecedorId),
              source: pedido.origemPorFornecedor?.[fornecedor.id] ?? fornecedor.source,
              isOnlyPendingSuggestion: fornecedor.isOnlyPendingSuggestion,
            }
          : {
              id: String(fornecedor.id),
              offerId: '',
              companyName: fornecedor.nome,
              status: QuotationMapSupplierStatus.PENDING,
              respondedAt: '',
              validity: '',
              price: 0,
              observations: undefined,
              selected: false,
              negotiatedPrice: undefined,
              negotiatedSelected: undefined,
              paymentMethod: undefined,
              paymentTerms: undefined,
              email: info?.email,
              link: info?.link,
              notParticipating,
              cnpj,
              phone,
              suggestedPending,
              ofertaFornecedorId: String(fornecedor.ofertaFornecedorId),
              source: pedido.origemPorFornecedor?.[fornecedor.id] ?? fornecedor.source,
              isOnlyPendingSuggestion: fornecedor.isOnlyPendingSuggestion,
            };

        suppliers.set(String(fornecedor.id), supplierData);
      }

      return {
        code: String(pedido.id),
        productId: String(pedido.produtoId),
        product: pedido.title ?? '',
        ERPId: this.extractOrderErpCode(pedido.atributos),
        branch: branchLabel({
          branch: pedido.endereco?.codigoFilial ?? undefined,
          branchName: pedido.endereco?.apelido ?? undefined,
        }),
        ...this.toDeliveryAddressLines(pedido.endereco),
        additionalInformation: pedido.informacoesAdicionais ?? undefined,
        quantity: pedido.qtd,
        unit: pedido?.unidadeMedida ?? 'UN',
        suppliers,
        purchaseHistory:
          pedido.historicoCompras &&
          this.toPurchaseHistory(pedido.historicoCompras, pedido.unidadeMedida ?? undefined),
      };
    });

    this.markBestPriceSuppliers(quotationMapItems);
    return quotationMapItems;
  }

  public toQuotationMapSupplierFreights(
    data: FindAllPurchaseRequestOffersResponseDto,
  ): Map<string, QuotationMapSupplierFreight> {
    const supplierFreights = new Map<string, QuotationMapSupplierFreight>();

    for (const fornecedor of data.fornecedores) {
      const supplierId = String(fornecedor.id);
      supplierFreights.set(supplierId, {
        supplierId,
        freight: Number(fornecedor.frete ?? 0),
        selected: false,
        negotiatedFreight: null,
        negotiatedSelected: false,
      });
    }

    return supplierFreights;
  }

  markBestPriceSuppliers(items: QuotationMapItem[]): void {
    items.forEach((item) => {
      let bestPrice = Infinity;
      let bestSupplierKey = '';

      item.suppliers.forEach((supplier, key) => {
        if (supplier.status === QuotationMapSupplierStatus.RESPONDED && supplier.price > 0) {
          if (supplier.price < bestPrice) {
            bestPrice = supplier.price;
            bestSupplierKey = key;
          }
        }
      });

      item.suppliers.forEach((supplier, key) => {
        supplier.bestPrice = key === bestSupplierKey;
      });
    });
  }

  toQuotationMap(
    offerDetails: HttpResponse<FIndOfferResponseDto>,
    offers: HttpResponse<FindAllPurchaseRequestOffersResponseDto>,
    suppliersInfo: SupplierInfo[] = [],
  ): HttpResponse<QuotationMapData> {
    const mergedData: FindAllPurchaseRequestOffersResponseDto = {
      ...offers.body!,
      pedidos: offers.body!.pedidos.map((p) => {
        const detail = offerDetails.body!.pedidos?.find((dp) => dp.id === p.id);
        return {
          ...p,
          atributos: p.atributos?.length ? p.atributos : detail?.atributos,
        };
      }),
    };

    const items = this.toQuotationMapItems(
      mergedData,
      suppliersInfo,
      offerDetails.body?.fornecedoresParticipantes ?? [],
    );
    const supplierIds = items.length ? Array.from(items[0].suppliers.keys()) : [];
    const supplierFreights = this.toQuotationMapSupplierFreights(mergedData);

    const newBody: QuotationMapData = {
      description: offerDetails.body!.description,
      id: String(offerDetails.body!.id),
      title: offerDetails.body!.title,
      requestedAt: new Date(offerDetails.body!.inicioCotacao),
      status: PurchaseRequestStatus.IN_QUOTATION,
      items,
      supplierIds,
      supplierFreights,
      lastSupplierSuggestionSearchAt: offerDetails.body!.ultimaBuscaFornecedoresSugeridosAt
        ? new Date(offerDetails.body!.ultimaBuscaFornecedoresSugeridosAt)
        : null,
    };

    return new HttpResponse<QuotationMapData>({
      body: newBody,
      headers: offers.headers,
      status: offers.status,
      statusText: offers.statusText,
      url: offers.url ?? '',
    });
  }

  public toApiOrderQueryStatus(
    status?: PurchaseRequestStatus | PurchaseItemStatus,
  ): string | undefined {
    if (!status) return undefined;
    if (status === PurchaseRequestStatus.PENDING || status === PurchaseItemStatus.PENDING)
      return 'PENDENTE';
    if (status === PurchaseRequestStatus.ACCEPTED || status === PurchaseItemStatus.APPROVED)
      return 'APROVADO';
    if (status === PurchaseItemStatus.REMOVED) return 'REMOVIDO';
    return undefined;
  }

  fromPurchaseStatus(status: PurchaseRequestStatus): string {
    const reverseStatusMap: { [key in PurchaseRequestStatus]: string } = {
      [PurchaseRequestStatus.PENDING]: 'pendente',
      [PurchaseRequestStatus.AWAITING_FOR_APPROVAL]: 'aguardando_aprovacao',
      [PurchaseRequestStatus.ACCEPTED]: 'aceita',
      [PurchaseRequestStatus.IN_QUOTATION]: 'pendente',
      [PurchaseRequestStatus.QUOTED]: 'processada',
      [PurchaseRequestStatus.URGENT]: 'pendente',
      [PurchaseRequestStatus.PENDING_INTEGRATION]: 'pendente de integração',
    };
    return reverseStatusMap[status] || 'pendente';
  }

  toPurchaseStatus(apiStatus: string): PurchaseRequestStatus[] {
    const statusMap: { [key: string]: PurchaseRequestStatus } = {
      pendente: PurchaseRequestStatus.PENDING,
      aprovacao_pendente: PurchaseRequestStatus.AWAITING_FOR_APPROVAL,
      aceita: PurchaseRequestStatus.ACCEPTED,
      em_cotacao: PurchaseRequestStatus.IN_QUOTATION,
      nao_completada: PurchaseRequestStatus.PENDING_INTEGRATION,
      cotacao_finalizada: PurchaseRequestStatus.QUOTED,
    };
    const status = statusMap[apiStatus];
    return status ? [status] : [];
  }

  toRequester(userId: number, usuario: { nome: string; avatar: string }) {
    return {
      id: userId.toString(),
      name: usuario.nome,
      avatarUrl: usuario.avatar,
    };
  }

  toPurchaseHistory(dto: HistoricoComprasDto, unit?: string): PurchaseHistory {
    return {
      unitLabel: unit || 'un',
      lastPurchasePrice: dto.ultimaCompra?.precoUnitario,
      bestPrice: dto.melhorPrecoHistorico,
      averageQuantity: dto.quantidadeMediaComprada,
      lastPurchaseDate: dto.ultimaCompra?.data,
      lastPurchaseSupplierName: dto.ultimaCompra?.fornecedorNome,
      previousSuppliers: (dto.fornecedoresQueJaForneceram ?? []).map((f) => ({
        id: String(f.id),
        name: f.nomeFantasia || f.nome,
      })),
    };
  }

  toSupplier(fornecedor: {
    id: number;
    ofertaFornecedorId: number;
    nome: string;
    cnpj: string;
    telefone: string;
    email: string;
    source?: string;
    status?: string;
  }): PurchaseSupplier {
    return {
      id: fornecedor.ofertaFornecedorId.toString(),
      name: fornecedor.nome,
      fantasyName: fornecedor.nome,
      cnpj: fornecedor.cnpj,
      phone: fornecedor.telefone,
      email: fornecedor.email,
      isVerified: false,
      source: fornecedor.source as PurchaseSupplierSource | undefined,
      linkStatus: fornecedor.status as PurchaseSupplierLinkStatus | undefined,
    };
  }

  toAcceptOffer(data: ApproveQuotationMap): AcceptOffersRequestDto {
    const body: AcceptOffersRequestDto = {
      aguardarAprovacao: data.awaitApproval ?? true,
      ofertas: data.items.map((offer) => ({
        tradingId: Number(offer.offerId),
        precoNegociado: offer.price,
      })),
      ofertaEstruturadaId: Number(data.purchaseId),
    };

    if (data.negotiatedFreights?.length) {
      body.fretesNegociados = data.negotiatedFreights.map((freight) => ({
        fornecedorId: Number(freight.supplierId),
        freteNegociado: freight.freight,
      }));
    }

    return body;
  }

  public toItemsFromOffer(dto: FIndOfferResponseDto): SendPurchaseRequestForQuoteItem[] {
    const pedidos = dto.pedidos || [];
    const fornecedoresParticipantes = dto.fornecedoresParticipantes || [];

    return pedidos.map((pedido) => {
      const productId = pedido.productId ?? pedido.produtoId ?? 0;

      const idERP = this.extractOrderErpCode(pedido.atributos);

      const costCenterAttr = pedido.atributos?.find((a) => {
        const name = a.nome?.toLowerCase() || '';
        return name.includes('centro de custo');
      });
      const costCenter = costCenterAttr?.valor ? [costCenterAttr.valor] : [];

      const unitAttr = pedido.atributos?.find((a) => {
        const name = a.nome?.toLowerCase() || '';
        return name.includes('unidade de medida') || name.includes('unidade');
      });
      const unit = unitAttr?.valor || 'UN';

      const specAttr = pedido.atributos?.find((a) => {
        const name = a.nome?.toLowerCase() || '';
        return name.includes('especificacao') || name.includes('informacoes adicionais');
      });
      const specification = specAttr?.valor || '-';

      let productName = pedido.title || '';
      if (!productName) {
        for (const fp of fornecedoresParticipantes) {
          const matchedProd = fp.produtos?.find(
            (p) => p.produtoId === productId || p.produto?.id === productId,
          );
          if (matchedProd?.produto?.title) {
            productName = matchedProd.produto.title;
            break;
          }
        }
      }

      const suppliers: PurchaseSupplier[] = [];
      const addedSupplierIds = new Set<number>();

      for (const fp of fornecedoresParticipantes) {
        const matchedProduct = fp.produtos?.find(
          (p) => p.produtoId === productId || p.produto?.id === productId,
        );
        if (matchedProduct && fp.fornecedor && !addedSupplierIds.has(fp.fornecedor.id)) {
          addedSupplierIds.add(fp.fornecedor.id);
          suppliers.push({
            id: fp.id.toString(),
            name: fp.fornecedor.nome,
            fantasyName: fp.fornecedor.nome,
            cnpj: fp.fornecedor.cpf || '',
            phone: fp.fornecedor.telefone || '',
            email: fp.fornecedor.email,
            isVerified: fp.fornecedor.verified || false,
            source: (matchedProduct.source ?? fp.source) as PurchaseSupplierSource | undefined,
            linkStatus: matchedProduct.status as PurchaseSupplierLinkStatus | undefined,
          });
        }
      }

      const requesterUser = pedido.usuario || {
        id: dto.usuarioId,
        nome: 'Solicitante',
        avatar: '',
      };
      const requester = this.toRequester(requesterUser.id, {
        nome: requesterUser.nome,
        avatar: requesterUser.avatar || '',
      });

      return {
        orderId: pedido.id.toString(),
        productId: productId.toString(),
        ERPId: idERP,
        branch: '',
        name: productName || '-',
        specification,
        costCenter,
        requester,
        unit,
        quantity: pedido.qtd,
        suppliers,
      };
    });
  }

  toPurchase(dto: HttpResponse<FIndOfferResponseDto>): HttpResponse<PurchaseRequestResponseDto> {
    const offer = dto.body!;
    const items = this.toItemsFromOffer(offer);

    const body: PurchaseRequestResponseDto = {
      id: offer.id.toString(),
      status: this.toPurchaseStatus(offer.status),
      title: offer.title,
      description: offer.description,
      costCenter: '',
      purchases: [],
      requestedAt: new Date(offer.createdAt),
      items,
      lastSupplierSuggestionSearchAt: offer.ultimaBuscaFornecedoresSugeridosAt
        ? new Date(offer.ultimaBuscaFornecedoresSugeridosAt)
        : null,
    };

    return new HttpResponse<PurchaseRequestResponseDto>({
      body,
      headers: new HttpResponse().headers,
      status: dto.status,
      statusText: dto.statusText,
      url: dto.url ?? '',
    });
  }

  fromSpreadSheetStatus(status: PurchaseItemStatus): SpreadSheetStatusDto {
    const reverseStatusMap: { [key in PurchaseItemStatus]: SpreadSheetStatusDto } = {
      [PurchaseItemStatus.PENDING]: SpreadSheetStatusDto.pendente,
      [PurchaseItemStatus.APPROVED]: SpreadSheetStatusDto.aceita,
      [PurchaseItemStatus.DECLINED]: SpreadSheetStatusDto.recusada,
      [PurchaseItemStatus.REMOVED]: SpreadSheetStatusDto.cancelada,
    };

    return reverseStatusMap[status] || SpreadSheetStatusDto.pendente;
  }

  toSpreadSheetStatus(status: string): PurchaseItemStatus {
    const statusMap: { [key: string]: PurchaseItemStatus } = {
      pendente: PurchaseItemStatus.PENDING,
      aprovado: PurchaseItemStatus.APPROVED,
      recusado: PurchaseItemStatus.DECLINED,
    };

    return statusMap[status] || PurchaseItemStatus.PENDING;
  }

  toStageTimes(dto: PurchaseStageTimesResponseDto): TimePerStageMetrics {
    const result: TimePerStageMetrics = {};

    const mapping: Record<keyof PurchaseStageTimesResponseDto, PurchaseRequestStatus> = {
      pendente: PurchaseRequestStatus.PENDING,
      aceita: PurchaseRequestStatus.ACCEPTED,
      em_cotacao: PurchaseRequestStatus.IN_QUOTATION,
      nao_completada: PurchaseRequestStatus.PENDING_INTEGRATION,
      cotacao_finalizada: PurchaseRequestStatus.QUOTED,
      aguardando_aprovacao: PurchaseRequestStatus.AWAITING_FOR_APPROVAL,
      aprovacao_pendente: PurchaseRequestStatus.AWAITING_FOR_APPROVAL,
    };

    Object.keys(mapping).forEach((key) => {
      const dtoKey = key as keyof PurchaseStageTimesResponseDto;
      const enumKey = mapping[dtoKey];
      const value = dto[dtoKey];

      if (typeof value === 'number') {
        result[enumKey] = (result[enumKey] || 0) + value;
      }
    });

    return result;
  }

  toPurchaseWithSpreadSheet(dto: FindAllPurchaseOffersResponseDto): PurchaseRequestResponseDto {
    const status = this.toPurchaseStatus(dto.status);
    const temPlanilha = (dto.planilhaPedidos || []).length > 0;
    const purchases: PurchaseRequest[] = temPlanilha
      ? (dto.planilhaPedidos || []).map((planilha) => ({
          id: planilha.id.toString(),
          requestedAt: new Date(planilha.createdAt),
          requester: this.toRequester(dto.usuarioId, dto.usuario),
          status: this.toSpreadSheetStatus(planilha.status),
          file: {
            name: `Planilha #${planilha.id}`,
            url: planilha.urlPlanilha,
            branchId: planilha.enderecoId.toString(),
            ...this.toBranchSummary(planilha.endereco),
          },
        }))
      : Array.from(
          (dto.pedidos || [])
            .reduce((map, pedido) => {
              const key = `${pedido.usuarioId}-${pedido.enderecoId}`;
              if (!map.has(key)) {
                map.set(key, {
                  id: pedido.id.toString(),
                  requestedAt: new Date(dto.createdAt),
                  requester: this.toRequester(pedido.usuarioId, pedido.usuario),
                  status: this.toSpreadSheetStatus(
                    pedido.status ? pedido.status.toLowerCase() : 'aprovado',
                  ),
                  file: {
                    name: `Pedido #${pedido.id}`,
                    url: '',
                    branchId: pedido.enderecoId.toString(),
                    ...this.toBranchSummary(pedido.endereco),
                  },
                });
              }
              return map;
            }, new Map<string, PurchaseRequest>())
            .values(),
        );

    const endDate = new Date(dto.end);
    const today = new Date();
    const remainingTime = endDate.getTime() - today.getTime();
    // Negativo = dias desde que o prazo encerrou (o card mostra "Prazo encerrado há N dias").
    const remainingDays = Math.ceil(remainingTime / (1000 * 60 * 60 * 24));
    // Alerta só para prazo iminente: hoje ou nos próximos 2 dias.
    const isDeadlineWarning = remainingDays >= 0 && remainingDays <= 2;

    return {
      id: dto.id.toString(),
      status,
      title: dto.title,
      description: dto.description,
      costCenter: dto.centroCusto?.[0] || '',
      purchases,
      requestedAt: new Date(dto.createdAt),
      hasDuplicatedProducts: dto.temPedidoDuplicado,
      hasPendingSuggestedSuppliers: dto.hasPendingSuggestedSuppliers,
      suppliers: dto.suppliers?.map((s) => ({
        id: s.id.toString(),
        name: s.nome,
        email: s.email,
        link: '',
      })),
      pendingSuppliers: dto.pendingSuppliers?.map((s) => ({
        id: s.id.toString(),
        name: s.nome,
        email: s.email,
        link: '',
      })),
      remainingDays,
      isDeadlineWarning,
      timePerStage: this.toStageTimes(dto.temposPorEtapa),
      orders: (dto.pedidosIntegrados || []).map((pedido) =>
        this.toPurchaseOrderHistory(pedido, dto.id),
      ),
      buyer: dto.approver
        ? {
            id: dto.approver.id.toString(),
            responsibleName: dto.approver.nome,
            avatarUrl: dto.approver.avatar,
          }
        : undefined,
      urlRelatorioAuditoria: dto.urlRelatorioAuditoria,
    };
  }

  toPurchaseOrderHistory(dto: PurchaseOrdersHistory, offerId: number): OrderHistoryItem {
    return {
      id: offerId,
      supplierName: dto.nomeFornecedor,
      orderCode: dto.codErp,
      products: dto.produtos.map((produto) => ({
        id: produto.id,
        nameProduct: produto.nomeProduto,
      })),
    };
  }

  toItemForQuote(dto: FindAllPurchaseProductsResponseDto): SendPurchaseRequestForQuoteItem {
    return {
      orderId: dto.id.toString(),
      productId: dto.productId.toString(),
      ERPId: dto.product.idERP || this.extractOrderErpCode(dto.atributos),
      branch: dto.endereco.codigoFilial || '',
      branchName: dto.endereco.apelido || undefined,
      ...this.toDeliveryAddressLines(dto.endereco),
      name: dto.product.title,
      specification: dto.informacoesAdicionais !== '' ? (dto.informacoesAdicionais ?? '-') : '-',
      costCenter: dto.centroCusto,
      requester: this.toRequester(dto.usuarioId, dto.usuario),
      unit: dto?.unidadeMedida ?? 'un',
      quantity: dto.qtd,
      suppliers: dto.product.fornecedores.map((fornecedor) => this.toSupplier(fornecedor)),
    };
  }

  maptoDuplicatedProductResponse(
    dto: CheckDuplicatedProductsResponseDto,
  ): DuplicatedProductResponseDto {
    const requester = this.toRequester(dto.usuario.id, dto.usuario);
    const suppliers = dto.fornecedores.map((f) => this.toSupplier(f));

    const productItem: SendPurchaseRequestForQuoteItem = {
      orderId: dto.produto.id.toString(),
      productId: dto.produto.id.toString(),
      ERPId: dto.produto.idERP,
      name: dto.produto.nome,
      specification: dto.informacoesAdicionais !== '' ? (dto.informacoesAdicionais ?? '-') : '-',
      costCenter: dto.centrosCusto,
      requester,
      unit: dto?.unidadeMedida ?? 'un',
      quantity: dto.qtd,
      suppliers,
    };

    return {
      orderId: dto.idPedido.toString(),
      purchaseId: dto.idOferta.toString(),
      productId: dto.produto.id.toString(),
      quantity: dto.qtd,
      requester,
      suppliers,
      product: productItem,
    };
  }

  toNewSupplierRequestDto(supplier: PurchaseSupplierCreate): NewSupplierRequestDto {
    return {
      nome: supplier.name,
      cnpj: supplier.cnpj,
      telefone: supplier.phone,
      email: supplier.email,
      inscricaoEstadual: supplier.stateRegistration,
      produtoId: supplier.productId ? Number(supplier.productId) : undefined,
      ofertaEstruturadaId: Number(supplier.purchaseId),
    };
  }

  toMergePurchasesRequestDto(input: MergePurchaseRequestItemsDto): MergePurchasesRequestDto {
    return {
      ofertaDestinoId: Number(input.purchaseId),
      pedidosOrigem: input.mergingItems.map((item) => ({
        pedidoId: Number(item.orderId),
        ofertaId: Number(item.originalPurchaseId),
      })),
    };
  }

  toSupplierInfo(data: SupplierInfoResponseDto): SupplierInfo {
    return {
      link: data.link,
      name: data.fornecedor,
      email: data.email,
    };
  }

  toTradingMatchSuggestion(data: TradingMatchSuggestionResponseDto): TradingMatchSuggestion {
    return {
      matchId: String(data.matchId),
      purchaseOrderId: String(data.pedidoId),
      requestedProductTitle: data.produtoPedidoTitulo,
      originTradingId: String(data.tradingOrigemId),
      suggestedProductTitle: data.produtoTradingTitulo,
      price: data.preco,
      supplierId: String(data.fornecedorId),
      supplierName: data.fornecedorNome,
      similarityScore: data.scoreSimilaridade,
    };
  }

  toCustomerTradingFocItem(data: CustomerTradingFocResponseDto): CustomerTradingFocItem {
    return {
      supplierName: data.name,
      focUrl: data.url,
    };
  }

  toBuyer(dto: BuyerResponseDto): BuyerItem {
    return {
      id: dto.id.toString(),
      name: dto.nome,
      avatar: dto.avatar,
    };
  }

  /**
   * Rua e CEP do local de entrega de um item, para a dica do chip de locais. As rotas de itens
   * não devolvem o nome da cidade nem o estado (só os ids): quem completa é a tela, pelo id do
   * endereço (ver injectBranchCities).
   */
  private toDeliveryAddressLines(
    endereco?: {
      id?: number | null;
      addressLine1?: string | null;
      addressLine2?: string | null;
      cep?: string | null;
    } | null,
  ): { branchId?: string; branchStreet?: string; branchCity?: string } {
    if (!endereco) return {};

    const street = [endereco.addressLine1, endereco.addressLine2].filter(Boolean).join(', ');
    return {
      branchId: endereco.id != null ? String(endereco.id) : undefined,
      branchStreet: street || undefined,
      branchCity: endereco.cep ? `CEP ${endereco.cep}` : undefined,
    };
  }

  /** Nome e endereço curto da filial de um pedido/planilha, para o card do kanban. */
  private toBranchSummary(
    endereco?: PurchaseAddressResponseDto | null,
  ): Pick<PurchaseRequestFile, 'branchName' | 'branchStreet' | 'branchCity'> {
    if (!endereco) return {};

    const street = [endereco.addressLine1, endereco.addressLine2].filter(Boolean).join(', ');
    const cityUf = endereco.cidade ? [endereco.cidade, endereco.uf].filter(Boolean).join('/') : '';
    const city = [cityUf, endereco.cep ? `CEP ${endereco.cep}` : ''].filter(Boolean).join(' · ');

    return {
      branchName: endereco.apelido || undefined,
      branchStreet: street || undefined,
      branchCity: city || undefined,
    };
  }

  toPurchaseRequestBranch(endereco: Endereco): PurchaseRequestBranch {
    return {
      id: endereco.id,
      name: endereco.apelido,
    };
  }

  public toPurchaseRequestCommentDto(
    comment: PurchaseRequestCommentResponseDto,
    orderId: string,
  ): PurchaseRequestCommentDto {
    return {
      id: String(comment.id || Math.random()),
      createdAt: comment.createdAt || new Date().toISOString(),
      text: comment.content,
      user: {
        id: String(comment.author.id),
        name: comment.author.nome,
        avatarUrl: comment.author.avatar ?? '',
      },
      attachment: comment.attachment
        ? this.toPurchaseRequestCommentAttachment(comment.attachment, comment.type)
        : undefined,
    };
  }

  private toPurchaseRequestCommentAttachment(
    fileUrl: string,
    type: OfertaCommentAttachmentType | null,
  ): PurchaseRequestCommentAttachmentDto {
    const fileName = fileUrl.split('/').pop()?.split('?')[0] || fileUrl;

    const categoryByType: Record<OfertaCommentAttachmentType, FileCategory> = {
      image: 'image',
      audio: 'audio',
      xlsx: 'xlsx',
      pdf: 'doc',
      document: 'doc',
      video: 'doc',
      other: 'doc',
    };

    return {
      url: fileUrl,
      fileName,
      type: type ? categoryByType[type] : 'doc',
    };
  }

  public toPurchaseRequestCommentsResponse(
    response: HttpResponse<PurchaseRequestCommentResponseDto[]>,
    purchaseId: string,
  ): HttpResponse<PurchaseRequestCommentDto[]> {
    const body = (response.body || []).map((comment) =>
      this.toPurchaseRequestCommentDto(comment, purchaseId),
    );

    return response.clone({ body });
  }

  public toPurchaseRequestCommentResponse(
    response: HttpResponse<PurchaseRequestCommentResponseDto>,
    purchaseId: string,
    defaultText: string,
  ): HttpResponse<PurchaseRequestCommentDto> {
    return response.clone({
      body: this.toPurchaseRequestCommentDto(response.body!, purchaseId),
    });
  }

  /*toPurchase(item: FindAllPurchaseResponseDto): PurchaseRequestResponseDto {
    return {
      id: item.id.toString(),
      status: this.toPurchaseStatus(item.status),
      title: item.title,
      description: item.description,
      costCenter: item.centroCusto,
      requesters: [this.toRequester(item.usuarioId, item.usuario)],
      files: item.urlPlanilhasPedidos,
      requestedAt: new Date(item.createdAt),
      purchases: []
    };
  }*/

  toUserResponse(data: Usuario): UserResponse {
    return {
      id: data.accountId || data.id?.toString(),
      email: data.email,
      cpf: data.cpf || data.empresaPadrao?.cnpj,
      name: data.nome,
      userName: data.email,
      avatar: data.avatar,
      phone: data.telefone1 || data.telefone2,
      lastAccessAt: undefined,
      isDeleted: !data.aprovado || data.rejeitado || false,
      tenantId: undefined,
      createdAt: new Date(),
      updatedAt: new Date(),
      groupsName: [],
      stateRegistration: data.empresaPadrao?.ie,
    };
  }

  toUncompletedPurchaseDialogData(
    dto: UncompletedPurchaseResponseDto,
  ): ProductIntegrationDialogData {
    const products: ProductIntegrationData[] = dto.pedidos.map((orders) => {
      const trading = orders.pedidoTradings[0];
      const suppliers = orders.pedidoTradings.map((t) => t.trading.usuario.nome);
      const costCenters = orders.atributos
        ? orders.atributos.filter((a) => a.nome === 'Centro de Custo').map((a) => a.valor)
        : [];

      const branch = orders.endereco?.apelido || orders.endereco?.codigoFilial;
      return {
        productCode: orders.product.id.toString(),
        productName: orders.product.title,
        ERPId: this.extractOrderErpCode(orders.atributos),
        status:
          trading.status === 'nao_completada'
            ? ProductIntegrationStatus.INTEGRATION_FAILED
            : ProductIntegrationStatus.SUCCESSFULLY_INTEGRATED,
        offerId: trading.tradingId.toString(),
        orderId: trading.externalOrderId,
        errorMessage: trading.errorMessage,
        suppliers,
        costCenters,
        branches: branch ? [branch] : undefined,
      };
    });

    const uniqueSuppliers = new Set<string>();
    dto.pedidos.forEach((p) => {
      p.pedidoTradings.forEach((t) => uniqueSuppliers.add(t.trading.usuario.nome));
    });

    return {
      title: dto.title,
      requestStatus: PurchaseRequestStatus.PENDING_INTEGRATION,
      purchaseId: dto.id.toString(),
      products,
    };
  }

  toUploadOfferDocumentsResult(
    error?: HttpErrorResponse,
  ): PurchaseRequestDocumentUploadItemResult[] {
    if (!error) {
      return [
        { type: PurchaseRequestDocumentUploadItemType.SPREADSHEET, success: true },
        { type: PurchaseRequestDocumentUploadItemType.SUPPLIER_QUOTE, success: true },
      ];
    }

    const { errors, message } = error.error || {};

    const spreadsheetErrors = errors?.planilha || (!errors && message ? [message] : undefined);
    const supplierQuoteErrors = errors?.pdf || (!errors && message ? [message] : undefined);

    return [
      {
        type: PurchaseRequestDocumentUploadItemType.SPREADSHEET,
        success: !spreadsheetErrors || spreadsheetErrors.length === 0,
        errors: spreadsheetErrors,
      },
      {
        type: PurchaseRequestDocumentUploadItemType.SUPPLIER_QUOTE,
        success: !supplierQuoteErrors || supplierQuoteErrors.length === 0,
        errors: supplierQuoteErrors,
      },
    ];
  }

  toAverageSlaLabels(dto: StageTimeMetricsResponseDto): TimePerStageMetrics {
    const keyMap: Record<string, PurchaseRequestStatus | null> = {
      pendente: PurchaseRequestStatus.PENDING,
      aceita: PurchaseRequestStatus.ACCEPTED,
      em_cotacao: PurchaseRequestStatus.IN_QUOTATION,
      cotacao_finalizada: null,
      nao_completada: PurchaseRequestStatus.PENDING_INTEGRATION,
      aprovacao_pendente: PurchaseRequestStatus.AWAITING_FOR_APPROVAL,
    };

    const result: TimePerStageMetrics = {};

    for (const [key, status] of Object.entries(keyMap)) {
      if (!status) continue;
      const mediaHoras = dto[key as keyof StageTimeMetricsResponseDto]?.mediaHoras;
      if (mediaHoras == null) continue;
      result[status] = mediaHoras;
    }

    return result;
  }

  public toPurchasingEvent(event: {
    ofertaId?: number;
    empresaId: number;
    tipo: string;
    encontrouFornecedor?: boolean;
  }): PurchasingEvent {
    const typeMap: Record<string, string> = {
      nova_oferta: 'new_offer',
      status_mudou: 'status_changed',
      fornecedor_respondeu: 'supplier_responded',
      comprador_alterado: 'buyer_changed',
      infos_alteradas: 'info_changed',
      comentario_criado: 'comment_created',
      pedido_status_mudou: 'item_status_changed',
    };

    return {
      purchaseId: event.ofertaId?.toString(),
      tenantId: event.empresaId,
      type: typeMap[event.tipo] || event.tipo,
      encontrouFornecedor: event.encontrouFornecedor,
    };
  }

  public toPurchaseRequestAudit(dto: PurchaseRequestAuditResponseDto): PurchaseRequestAuditDto {
    return {
      id: dto.id,
      action: dto.action,
      message: dto.message,
      status: dto.status as PurchaseRequestAuditStatus,
      createdAt: typeof dto.createdAt === 'string' ? dto.createdAt : dto.createdAt.toISOString(),
      author: dto.author
        ? { name: dto.author.name, avatarUrl: dto.author.avatar }
        : { name: 'Sistema' },
    };
  }

  public toAddSupplierToProductRequestDto(
    data: AddSupplierToProductDto,
    cachedSupplier?: Partial<QuotationSupplierCacheEntry>,
  ): NewSupplierRequestDto {
    return {
      ofertaEstruturadaId: Number(data.purchaseId),
      produtoId: Number(data.productId),
      nome: data.supplierName || cachedSupplier?.nome || '',
      cnpj: data.supplierCnpj || cachedSupplier?.cnpj || '',
      telefone: data.supplierPhone || cachedSupplier?.telefone || '',
      email: data.supplierEmail || cachedSupplier?.email || '',
      inscricaoEstadual: data.stateRegistration || cachedSupplier?.inscricaoEstadual || 'ISENTO',
    };
  }
}
